import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_VALIDATION_TYPES_RESOURCE } from 'src/features/validationTypes/api/useListValidationTypes';
import { useUpdateValidationType } from 'src/features/validationTypes/api/useUpdateValidationType';
import ValidationTypeFormModal from 'src/features/validationTypes/components/forms/ValidationTypeFormModal';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { ValidationType } from 'src/features/validationTypes/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateValidationTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedType: ValidationType;
    children?: React.ReactNode;
}

function UpdateValidationTypeFormModal<TData>({
    className,
    selectedType,
    children,
}: UpdateValidationTypeFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: updateValidationType, isPending } = useUpdateValidationType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VALIDATION_TYPES_RESOURCE] });
            toast({
                title: formatMessage(validationTypesMessages.updateValidationTypeSuccess),
                description: formatMessage(validationTypesMessages.updateValidationTypeSuccessDescription),
            });
        },
    });

    return (
        <ValidationTypeFormModal
            className={className}
            selectedType={selectedType}
            mutate={(values, onSuccess, onError) =>
                updateValidationType({ ...values, id: selectedType.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(validationTypesMessages.editValidationType)}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(validationTypesMessages.editValidationTypeSubtitle, {
                type: selectedType.name,
            })}
        >
            {children}
        </ValidationTypeFormModal>
    );
}

export default UpdateValidationTypeFormModal;
